// ECharts option builders and tooltip rows for <vf-income-explorer> levels
// 1 and 2. Data + theme in, plain option objects and TooltipLine rows out —
// no DOM construction, no chart instance handling.
//
// Colors are the explorer channel aliases (theme.ts channelFees /
// channelTips / channelMev / channelBam): dedicated tokens initialized to
// the series inks they visually matched (medianLine, bandEdge, netLine,
// policyNote) but decoupled — a main-chart series retune must not silently
// recolor these lamport channels, and vice versa. Level-2 fees+tips bars
// and their tooltip sum use channelFees. Bar heights are the
// display-edge SOL conversion (income-explorer.ts solNumber); every visible
// figure formats from the integer via format.ts lamportsToSol.

import type { BarSeriesOption } from "echarts/charts";
import type {
  GridComponentOption,
  LegendComponentOption,
  TooltipComponentOption,
} from "echarts/components";
import type { ComposeOption } from "echarts/core";

import type { ApyRow, IncomeRow } from "./api";
import { MONO_FONT, SANS_FONT } from "./chart-options";
import { formatCount, lamportsToSol } from "./format";
import { NOT_SERVED_TEXT, solNumber, type ExplorerBlockRow } from "./income-explorer";
import type { ThemeTokens } from "./theme";
import type { EpochTooltipView, TooltipLine } from "./tooltip";

export type VfExplorerOption = ComposeOption<
  BarSeriesOption | GridComponentOption | LegendComponentOption | TooltipComponentOption
>;

/** One epoch's income channels: /income row joined with its /apy row by epoch. */
export interface EpochChannelPoint {
  epoch: number;
  blockFeesLamports: number | null;
  tipsLamports: number | null;
  /** Jito's post-epoch settled tips figure (/income tips_gross_kobe_lamports) — the second witness of the tips flow. */
  tipsKobeLamports: number | null;
  mevClaimedLamports: number | null;
  /** Served SOL valuation of the BAM boost entitlement (/apy bam_sol_lamports). */
  bamSolLamports: number | null;
  voteCostLamports: number | null;
  operatorTxCostLamports: number | null;
  /** Ledger net_total (/apy net_total_lamports). */
  netTotalLamports: number | null;
}

/**
 * One epoch's channel point from its raw /income and /apy rows. A missing
 * row leaves that side's channels null — rendered as written absence. Also
 * the host chart's income-view join (vf-apy-chart.ts), keyed off its
 * byEpoch map, so the tooltip decomposition is byte-identical to the
 * explorer's.
 */
export function epochChannelPoint(
  epoch: number,
  income: IncomeRow | undefined,
  apy: ApyRow | undefined,
): EpochChannelPoint {
  return {
    epoch,
    blockFeesLamports: income?.block_fees_lamports ?? null,
    tipsLamports: income?.tips_gross_lamports ?? null,
    tipsKobeLamports: income?.tips_gross_kobe_lamports ?? null,
    mevClaimedLamports: income?.mev_claimed_lamports ?? null,
    bamSolLamports: apy?.bam_sol_lamports ?? null,
    voteCostLamports: income?.vote_cost_lamports ?? null,
    operatorTxCostLamports: income?.operator_tx_cost_lamports ?? null,
    netTotalLamports: apy?.net_total_lamports ?? null,
  };
}

/**
 * Joins /income channel rows with /apy rows by epoch. /income anchors the
 * series (one point per served income row, epoch ascending); an epoch
 * without an /apy row keeps null BAM and net — rendered as written absence.
 */
export function joinEpochChannels(
  income: readonly IncomeRow[],
  apy: readonly ApyRow[],
): EpochChannelPoint[] {
  const apyByEpoch = new Map(apy.map((row) => [row.epoch, row]));
  return [...income]
    .sort((a, b) => a.epoch - b.epoch)
    .map((row) => epochChannelPoint(row.epoch, row, apyByEpoch.get(row.epoch)));
}

interface ChannelSpec {
  name: string;
  value: (p: EpochChannelPoint) => number | null;
  color: (t: ThemeTokens) => string;
}

/** The four stacked gross income channels, in stack order (bottom first). */
export const EPOCH_CHANNELS: readonly ChannelSpec[] = [
  { name: "block fees", value: (p) => p.blockFeesLamports, color: (t) => t.channelFees },
  { name: "tips", value: (p) => p.tipsLamports, color: (t) => t.channelTips },
  { name: "MEV claimed", value: (p) => p.mevClaimedLamports, color: (t) => t.channelMev },
  { name: "BAM boost", value: (p) => p.bamSolLamports, color: (t) => t.channelBam },
];

function solText(lamports: number | null): string {
  return lamports === null ? NOT_SERVED_TEXT : `${lamportsToSol(lamports)} SOL`;
}

/** Expense rendered as the subtraction it is: served value, minus sign. */
function negSolText(lamports: number | null): string {
  return lamports === null ? NOT_SERVED_TEXT : `${lamportsToSol(-lamports)} SOL`;
}

/**
 * Level-1 tooltip header: the epoch AND what the validator made in it —
 * the ledger net_total, the one figure the whole tooltip exists to
 * explain. Null renders as written absence in title position.
 */
export function epochTooltipTitle(p: EpochChannelPoint): string {
  const net = p.netTotalLamports === null ? NOT_SERVED_TEXT : `${lamportsToSol(p.netTotalLamports)} SOL`;
  return `Epoch ${p.epoch} · net ${net}`;
}

/**
 * Level-1 tooltip body: the net derivation, grouped so it reads as the
 * ledger equation — income (block fees + MEV claimed + BAM boost), minus
 * expenses (vote cost, operator tx cost, values signed negative), equals
 * net (the header figure, repeated with its derivation named). Tips sit
 * in their own trailing group because they are NOT the validator's
 * income: they flow to stakers-side tip accounts, and the validator's
 * share arrives as the MEV-claimed row above. Null stays written absence.
 */
export function epochTooltipView(p: EpochChannelPoint, t: ThemeTokens): EpochTooltipView {
  return {
    groups: [
      {
        label: "income",
        marker: null,
        rows: [
          { label: "block fees", value: solText(p.blockFeesLamports), color: t.channelFees },
          { label: "MEV claimed", value: solText(p.mevClaimedLamports), color: t.channelMev },
          { label: "BAM boost", value: solText(p.bamSolLamports), color: t.channelBam },
        ],
      },
      {
        label: "expenses",
        marker: null,
        rows: [
          { label: "vote cost", value: negSolText(p.voteCostLamports) },
          { label: "operator tx cost", value: negSolText(p.operatorTxCostLamports) },
        ],
      },
      {
        label: null,
        marker: null,
        rows: [{ label: "net = income - expenses (ledger net_total)", value: solText(p.netTotalLamports) }],
      },
      {
        label: "pass-through, not income",
        marker: null,
        rows: [
          {
            label: "tips (to stakers; our share is MEV claimed)",
            value: solText(p.tipsLamports),
            color: t.channelTips,
          },
          // Second witness of the same flow: Jito's post-epoch settled
          // figure, beside the block-observed row above. Same swatch hue
          // by design (two measurements of one flow, not two entities);
          // null stays written absence — never a fallback to the other.
          {
            label: "tips (Kobe-settled)",
            value: solText(p.tipsKobeLamports),
            color: t.channelTips,
          },
        ],
      },
    ],
    trailing: [],
  };
}

/** Level-2 tooltip: the block's served fees and tips plus their labeled sum. */
export function blockTooltipLines(b: ExplorerBlockRow, t: ThemeTokens): TooltipLine[] {
  return [
    { label: "slot", value: formatCount(b.slot) },
    { label: "fees", value: solText(b.feeRewardLamports) },
    { label: "tips", value: solText(b.tipsLamports) },
    {
      // channelFees carries the level-2 total for now (deliberate alias:
      // a dedicated sum token can split off if the operator wants the
      // block total inked apart from the fees channel).
      label: "fees + tips",
      value: solText(b.feeRewardLamports + b.tipsLamports),
      color: t.channelFees,
    },
  ];
}

export interface ExplorerChartInputs<Datum> {
  data: readonly Datum[];
  theme: ThemeTokens;
  /** ECharts axis-trigger tooltip formatter (closes over per-point data). */
  tooltipFormatter: (params: unknown) => string;
  /** Shadow-DOM container the tooltip renders into; omitted in DOM-less tests. */
  tooltipAppendTo?: HTMLElement;
  /** Host below the explorer's narrow rung: the epochs legend wraps, so the plot yields headroom. */
  narrow?: boolean;
}

function categoryXAxis(t: ThemeTokens, labels: string[]) {
  return {
    type: "category" as const,
    data: labels,
    axisLine: { lineStyle: { color: t.axisLine } },
    axisTick: { alignWithLabel: true },
    axisLabel: { fontSize: 11, fontFamily: MONO_FONT, color: t.axisText, hideOverlap: true },
    splitLine: { show: false },
  };
}

function solYAxis(t: ThemeTokens) {
  return {
    type: "value" as const,
    name: "SOL",
    nameTextStyle: { fontSize: 11, color: t.axisText, align: "right" as const },
    axisLabel: { fontSize: 11, fontFamily: MONO_FONT, color: t.axisText },
    axisLine: { show: true, lineStyle: { color: t.axisLine } },
    splitLine: { show: true, lineStyle: { color: t.splitLine } },
  };
}

function axisTooltip(
  t: ThemeTokens,
  formatter: (params: unknown) => string,
): TooltipComponentOption {
  return {
    trigger: "axis",
    confine: true,
    backgroundColor: t.tooltipBg,
    borderColor: t.tooltipBorder,
    textStyle: { color: t.text, fontSize: 12 },
    axisPointer: { type: "shadow" },
    // Same width guard as the main chart's tooltip (chart-options.ts):
    // confine bounds position, not width.
    extraCssText: "max-width:calc(100% - 16px);white-space:normal;",
    formatter: formatter as never,
  };
}

function withAppendTo(option: VfExplorerOption, appendTo: HTMLElement | undefined): VfExplorerOption {
  if (appendTo !== undefined && option.tooltip !== undefined && !Array.isArray(option.tooltip)) {
    option.tooltip.appendTo = appendTo;
  }
  return option;
}

/** Level 1: one stacked bar per epoch of the four gross income channels; a null channel is a gap. */
export function buildEpochsOption(i: ExplorerChartInputs<EpochChannelPoint>): VfExplorerOption {
  const t = i.theme;
  const option: VfExplorerOption = {
    animation: false,
    textStyle: { fontFamily: SANS_FONT },
    // Legend starts PAST the y-axis gutter: left-anchored at 4 it
    // overprinted the axis name "SOL" (operator screenshot 2026-07-14 —
    // same defect class as the income-view legend collision).
    legend: {
      top: 4,
      left: 68,
      itemWidth: 10,
      itemHeight: 10,
      icon: "rect",
      itemGap: 16,
      textStyle: { fontSize: 12, color: t.statsText },
    },
    // Narrow: the four-channel legend wraps to a second row inside
    // [68, right], so the plot starts lower — same seat logic as the
    // main chart's compact tier.
    grid: { left: 56, right: 16, top: i.narrow === true ? 64 : 40, bottom: 24, show: false },
    xAxis: categoryXAxis(t, i.data.map((p) => String(p.epoch))),
    yAxis: solYAxis(t),
    tooltip: axisTooltip(t, i.tooltipFormatter),
    series: EPOCH_CHANNELS.map((c) => ({
      name: c.name,
      type: "bar" as const,
      stack: "income",
      data: i.data.map((p) => {
        const v = c.value(p);
        return v === null ? null : solNumber(v);
      }),
      itemStyle: { color: c.color(t) },
      barMaxWidth: 28,
      cursor: "pointer",
      emphasis: { focus: "none" as const },
    })),
  };
  return withAppendTo(option, i.tooltipAppendTo);
}

export const BLOCKS_SERIES = "fees + tips";

/** Level 2: one bar per produced block, height = fees + tips, x by slot. */
export function buildBlocksOption(i: ExplorerChartInputs<ExplorerBlockRow>): VfExplorerOption {
  const t = i.theme;
  const option: VfExplorerOption = {
    animation: false,
    textStyle: { fontFamily: SANS_FONT },
    grid: { left: 56, right: 16, top: 16, bottom: 24, show: false },
    xAxis: categoryXAxis(t, i.data.map((b) => String(b.slot))),
    yAxis: solYAxis(t),
    tooltip: axisTooltip(t, i.tooltipFormatter),
    series: [
      {
        name: BLOCKS_SERIES,
        type: "bar" as const,
        data: i.data.map((b) => solNumber(b.feeRewardLamports + b.tipsLamports)),
        itemStyle: { color: t.channelFees },
        cursor: "pointer",
        emphasis: { focus: "none" as const },
      },
    ],
  };
  return withAppendTo(option, i.tooltipAppendTo);
}
